<template>
  <div class="container">
    <!-- 页面标题和菜单栏 -->
    <div class="container01">
      <h1 class="container01_h1">商品管理</h1>
      <!-- 菜单栏按钮 -->
      <el-row :gutter="20">
        <el-col :span="24" class="button-col">
          <div class="container01_button">
            <el-button @click="$router.back()">返回上一级</el-button>
            <el-button type="primary" @click="handleAddMerchandise">新增商品</el-button>
            <el-button type="primary" @click="handleBatchImport">批量导入</el-button>
            <el-button type="danger" @click="handleBatchDelete"
              :disabled="selectedMerchandiseIds.length === 0">删除选中商品</el-button>
            <el-button type="primary" @click="handleStockWarning">库存预警</el-button>
            <el-button type="primary" @click="handleCategoryManage" style="margin-right: 10px;">分类管理</el-button>
          </div>
        </el-col>
      </el-row>
      <!-- 搜索栏 -->
      <el-row :gutter="20">
        <el-col :span="24" class="search-col">
          <el-input v-model="searchKeyword" placeholder="请输入商品名称/规格，支持模糊搜索" clearable class="container01_search"
            @keyup.enter="handleSearch"></el-input>
          <el-button type="primary" @click="handleSearch" style="margin-top: 20px;">搜索</el-button>
        </el-col>
      </el-row>
    </div>
    <!-- 信息表格 -->
    <div class="container02">
      <div>
        <el-table :data="paginatedData" border style="width: 100%" height="calc(100vh - 200px)" v-loading="loading"
          element-loading-text="加载中" @selection-change="handleSelectionChange">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="merchandise_id" label="商品ID" width="100" />
          <el-table-column prop="name" label="商品名称" min-width="5%" />
          <el-table-column prop="specification" label="商品规格" min-width="5%" />
          <el-table-column prop="category" label="商品分类" min-width="10%" />
          <el-table-column label="操作" min-width="3%">
            <template #default="scope">
              <el-button type="text" @click="handleViewDetail(scope.row.merchandise_id)">详情</el-button>
              <el-button type="text" @click="handleDelete(scope.row.merchandise_id)"
                style="color: rgb(250,182,182);">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <!-- 分页按钮 -->
        <div class="pagination">
          <button @click="prevPage" :disabled="currentPage <= 1" class="page-button">上一页</button>
          <span class="page-info">当前第 {{ currentPage }} 页 / 共 {{ totalPages }} 页 - 共 {{ totalCount }} 条数据</span>
          <button @click="nextPage" :disabled="currentPage >= totalPages" class="page-button">下一页</button>
        </div>
      </div>
    </div>
    <!-- 新增商品弹窗 -->
    <el-dialog title="新增商品" v-model="addDialogVisible">
      <el-form :model="newMerchandise" ref="form">
        <el-form-item label="商品名称" required>
          <el-input v-model="newMerchandise.name"></el-input>
        </el-form-item>
        <el-form-item label="商品规格">
          <el-input v-model="newMerchandise.specification" placeholder="选填（如：330ml/瓶）"></el-input>
        </el-form-item>
        <el-form-item label="商品分类">
          <el-input v-model="newMerchandise.category" placeholder="选填（如：饮料/零食）"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveMerchandise">保存</el-button>
      </template>
    </el-dialog>
    <!-- 批量导入商品弹窗 -->
    <el-dialog title="批量导入商品" v-model="importDialogVisible">
      <div class="import-area">
        <input type="file" ref="fileInput" accept=".xlsx,.json" @change="handleFile">
        <button @click="triggerFile">选择Excel或JSON文件</button>
        <p v-if="fileName">{{ fileName }}</p>
      </div>
      <template #footer>
        <el-button @click="downloadTemplate">下载模板</el-button>
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="doImport" :disabled="!fileName">导入</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request';
import merchandiseAPI from '@/api/merchandises';

export default {
  data() {
    return {
      merchandises: [],
      searchKeyword: '',
      loading: false,
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      addDialogVisible: false,
      importDialogVisible: false,
      newMerchandise: { name: '', specification: '', category: '' },
      fileName: '',
      file: null,
      selectedMerchandiseIds: []
    }
  },
  created() {
    this.fetchMerchandisesData();
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize) || 1;
    },
    paginatedData() {
      return this.merchandises;
    }
  },
  methods: {
    async fetchMerchandisesData() {
      this.loading = true;
      try {
        const params = {
          keyword: this.searchKeyword,
          page: this.currentPage,
          per_page: this.pageSize
        };
        const originData = await merchandiseAPI.inquireMerchandises(params);
        const merchandiseList = Array.isArray(originData.merchandises) ? originData.merchandises : [];
        this.merchandises = merchandiseList;
        this.totalCount = originData.total || 0;
        this.currentPage = originData.page || 1;
      } catch (error) {
        console.error('获取商品数据失败：', error);
        this.$message.error('加载商品列表失败，请重试');
      } finally {
        this.loading = false;
      }
    },
    async saveMerchandise() {
      if (!this.newMerchandise.name.trim()) {
        this.$message.warning('商品名称不能为空');
        return;
      }
      try {
        await merchandiseAPI.createMerchandise(this.newMerchandise);
        this.$message.success('商品新增成功');
        this.addDialogVisible = false;
        this.newMerchandise = { name: '', specification: '', category: '' };
        this.fetchMerchandisesData();
      } catch (err) {
        console.error('新增商品失败：', err);
        this.$message.error('新增商品失败，请重试');
      }
    },
    triggerFile() {
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    handleFile(e) {
      const file = e.target.files[0];
      if (file) {
        const allowedTypes = ['application/json', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        const allowedExtensions = ['.json', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
          this.$message.error('仅支持 JSON 或 XLSX 格式的文件');
          this.file = null;
          this.fileName = '';
          return;
        }
        this.file = file;
        this.fileName = file.name;
      }
    },
    async doImport() {
      const formData = new FormData();
      formData.append('file', this.file);
      try {
        this.loading = true;
        await request.post('/api/merchandises/batch', formData, {
          timeout: 30000,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        this.$message.success('商品批量导入成功');
        this.importDialogVisible = false;
        this.fileName = '';
        this.fetchMerchandisesData();
        this.$refs.fileInput.value = '';
      } catch (err) {
        console.error('导入失败详情：', err);
        let errorMessage = '导入失败：';
        if (err.response) {
          const status = err.response.status;
          const data = err.response.data;
          switch (status) {
            case 400:
              errorMessage += data.message || '请求参数错误，请检查文件格式和数据';
              break;
            case 413:
              errorMessage += '文件太大，请压缩后重新上传';
              break;
            case 415:
              errorMessage += '不支持的文件类型';
              break;
            case 500:
              errorMessage += data.message || '服务器内部错误，请联系管理员';
              break;
            default:
              errorMessage += data.message || `服务器错误 (${status})`;
          }
        } else if (err.request) {
          errorMessage += '网络错误，无法连接到服务器，请检查网络连接';
        } else {
          errorMessage += err.message || '未知错误';
        }
        this.$message.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchMerchandisesData();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchMerchandisesData();
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchMerchandisesData();
    },
    handleAddMerchandise() {
      this.addDialogVisible = true;
    },
    handleBatchImport() {
      this.importDialogVisible = true;
    },
    handleViewDetail(merchandiseId) {
      this.$router.push(`/merchandises/detail/${merchandiseId}`);
    },
    handleStockWarning() {
      this.$message.info('库存预警功能待实现');
    },
    handleCategoryManage() {
      this.$message.info('分类管理功能待实现');
    },
    handleSelectionChange(selectedRows) {
      this.selectedMerchandiseIds = selectedRows.map(row => row.merchandise_id);
    },
    async handleDelete(merchandiseId) {
      try {
        await this.$confirm('确定要删除该商品吗？删除后数据不可恢复！', '删除确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        await merchandiseAPI.deleteMerchandise(merchandiseId);
        this.$message.success('商品删除成功');
        this.fetchMerchandisesData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败：', error);
          this.$message.error('删除商品失败，请重试');
        }
      }
    },
    async handleBatchDelete() {
      try {
        if (this.selectedMerchandiseIds.length === 0) {
          this.$message.warning('请先选中要删除的商品');
          return;
        }
        await this.$confirm(`确定要删除选中的 ${this.selectedMerchandiseIds.length} 个商品吗？删除后数据不可恢复！`, '批量删除确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        await merchandiseAPI.batchDeleteMerchandises(this.selectedMerchandiseIds);
        this.$message.success('批量删除成功');
        this.fetchMerchandisesData();
        this.selectedMerchandiseIds = [];
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败：', error);
          this.$message.error('批量删除商品失败，请重试');
        }
      }
    },
    downloadTemplate() {
      const headers = ['name', 'specification', 'category']
      const exampleRow = ['可口可乐', '330ml/瓶', '饮料']

      const csvContent = [
        headers.join(','),
        exampleRow.join(',')
      ].join('\n')

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', '商品批量导入模板.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.container {
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.container01_h1 {
  text-align: center;
  margin-bottom: 24px;
  font-size: 28px;
  font-weight: 600;
  background: linear-gradient(135deg, #1e293b, #3b82f6);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.container01_button {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
}

.search-col {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 20px;
}

.container01_search {
  flex: 1;
  min-width: 200px;
}

.container02 {
  width: 100%;
  margin-top: 20px;
  overflow-x: auto;
}

.el-table {
  border-radius: 16px;
  overflow: hidden;
}

.el-table__row:hover>td {
  background-color: #fef9e3 !important;
}

.el-button--text {
  margin: 0 4px;
  transition: all 0.2s;
}

.el-button--text:hover {
  transform: translateY(-1px);
  color: var(--primary-color);
}

.el-dialog .el-form-item {
  margin-bottom: 20px;
}

.el-dialog .el-input__inner {
  border-radius: 12px;
}

.import-area {
  padding: 20px;
  text-align: center;
  border: 2px dashed #e2e8f0;
  border-radius: 20px;
  background: #fafcff;
  transition: all 0.2s;
}

.import-area:hover {
  border-color: var(--primary-color);
  background: #f0f9ff;
}

.import-area button {
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 40px;
  padding: 8px 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.import-area button:hover {
  background: var(--primary-dark);
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .container {
    padding: 12px !important;
  }

  .container01_button {
    flex-direction: column;
    gap: 8px;
  }

  .search-col {
    flex-direction: column;
    align-items: stretch;
  }

  .container01_search {
    width: 100%;
    margin-right: 0;
    margin-top: 0;
  }

  .el-button {
    width: 100%;
    margin: 0 !important;
  }

  .container02 {
    width: 100%;
  }

  .el-table {
    font-size: 12px;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>